import { useEffect, useState } from 'react';
import BaseAppService from '../../../application/platform/scp/base/base-app-service';
import { useFeedback } from '../../../hooks/use-feedback';
import { hasErrors } from '../../../utils/validation';
import { IFileAddRequest } from '../../../model/scp/base/request/file-add-request';
import { IFileResponse } from '../../../model/scp/base/response/file-response';
import { ITableResponse } from '../../../model/scp/purchase/response/tables-response';
import { validateFileAttachment } from './file-attachment-validation';

export interface FileAttachmentsService {
    // Grupo de tipos de documento do PNCP (1 compra, 3 contrato, 6 IRP)
    documentTypeGroup: number;
    list: (id: number) => Promise<IFileResponse[]>;
    add: (request: IFileAddRequest) => Promise<unknown>;
    remove: (justification: string, id: number, number: number) => Promise<unknown>;
}

// Anexos publicados no PNCP de um registro já salvo (IRP, compra ou contrato)
export const useFileAttachments = (entityId: number | undefined, service: FileAttachmentsService) => {
    const { showError, showSuccess, confirmDeleteWithJustification, withLoading } = useFeedback();
    const [fileRequest, setFileRequest] = useState<IFileAddRequest>({ id: entityId });
    const [files, setFiles] = useState<IFileResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [typeDocument, setTypeDocument] = useState<ITableResponse[]>([]);

    useEffect(() => {
        BaseAppService.listTypeDocument(service.documentTypeGroup)
            .then(setTypeDocument)
            .catch((error: any) => showError('Não foi possível carregar os tipos de documento', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId]);

    async function loadFiles() {
        if (entityId === undefined) return;
        setLoading(true);
        try {
            setFiles(await service.list(entityId));
        } catch (error: any) {
            showError('Não foi possível carregar os arquivos', error);
        } finally {
            setLoading(false);
        }
    }

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'input-title-atachment') setFileRequest((previous) => ({ ...previous, title: value }));
        if (name === 'select-document-type-atachment') setFileRequest((previous) => ({ ...previous, documentTypeId: value === '' ? undefined : Number(value) }));
    }

    function setFileUpload(document: File) {
        setFileRequest((previous) => ({ ...previous, document }));
    }

    async function saveFile() {
        if (isSaving || entityId === undefined) return;
        if (hasErrors(validateFileAttachment(fileRequest))) {
            setShowValidationErrors(true);
            return;
        }
        setIsSaving(true);
        try {
            await withLoading(() => service.add(fileRequest));
            showSuccess('Documento adicionado com sucesso!');
            setFileRequest({ id: entityId });
            setShowValidationErrors(false);
            await loadFiles();
        } catch (error: any) {
            showError('Não foi possível adicionar o arquivo', error);
        } finally {
            setIsSaving(false);
        }
    }

    function deleteFile(file: IFileResponse) {
        if (entityId === undefined || file.number === undefined) return;
        const fileNumber = file.number;
        confirmDeleteWithJustification(`Tem certeza que deseja excluir o anexo "${file.title}"?`, 'Informe a justificativa para excluir o anexo', async (justification) => {
            try {
                await withLoading(() => service.remove(justification, entityId, fileNumber));
                showSuccess('Anexo excluído com sucesso!');
                await loadFiles();
            } catch (error: any) {
                showError('Não foi possível excluir o arquivo', error);
            }
        });
    }

    return {
        fileRequest,
        files,
        loading,
        isSaving,
        typeDocument,
        validationErrors: showValidationErrors ? validateFileAttachment(fileRequest) : {},
        handleOnChange,
        setFileUpload,
        saveFile,
        deleteFile,
    };
};
